import * as XLSX from 'xlsx';
import {
  RuntimeSurveyQuestion,
  SurveyResponseDetail,
  getSurveyQuestionsBySurveyId,
  getSurveyResponseByCustomerIdentity,
} from '../Survey/surveyApi';

function getAnswerText(detail: SurveyResponseDetail, question: RuntimeSurveyQuestion): string {
  const selectedOptions = detail.surveyResponseDetailsOptionsDTOList ?? [];

  if (selectedOptions.length > 0) {
    const selectedIds = new Set(selectedOptions.map((o) => {return o.questionOptionId}));
    const titles = question.options
      .filter((o) => {return selectedIds.has(o.questionOptionId)})
      .map((o) => {return o.optionTitle});

    if (titles.length > 0) {
      return titles.join(', ');
    }
  }

  return detail.response ?? '';
}

export async function downloadSurveyExcel(args: {
  customerNumber: string;
  fundingType: string;
  reportingYear: number;
  surveyId: number;
  employerGroupName: string;
  surveyYear: string;
}): Promise<void> {
  const [questions, surveyResponse] = await Promise.all([
    getSurveyQuestionsBySurveyId({ surveyId: args.surveyId }),
    getSurveyResponseByCustomerIdentity({
      customerNumber: args.customerNumber,
      fundingType: args.fundingType,
      reportingYear: args.reportingYear,
      surveyId: args.surveyId,
    }),
  ]);

  if (!surveyResponse) {
    throw new Error('No survey response found for this customer.');
  }

  const questionMap = new Map<number, RuntimeSurveyQuestion>(
    questions.map((q) => {return [q.questionId, q]}),
  );

  const sortedDetails = [...surveyResponse.surveyResponseDetailsDTOList].sort((a, b) => {
    const qa = questionMap.get(a.questionId);
    const qb = questionMap.get(b.questionId);
    return (qa?.orderId ?? 0) - (qb?.orderId ?? 0);
  });

  const dataRows: [string, string][] = [];

  for (const detail of sortedDetails) {
    const question = questionMap.get(detail.questionId);

    if (!question) {
      continue;
    }

    const answer = getAnswerText(detail, question);

    if (!answer.trim()) {
      continue;
    }

    dataRows.push([question.questionTitle, answer]);
  }

  const sheetData = [['Question', 'Answer'], ...dataRows];
  const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

  worksheet['!cols'] = [{ wch: 60 }, { wch: 80 }];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'RFI Responses');

  const safeGroupName = args.employerGroupName.replace(/[/\\?*[\]:]/g, '_');
  const filename = `${args.surveyYear} RFI - ${safeGroupName}.xlsx`;

  XLSX.writeFile(workbook, filename);
}
